import { getConfig, ENV } from './baseConfig';

// For External service endpoint.
// **Please keep in alphabetical order.
const AZURE_SSO_REDIRECT = '/azure/auth';
const GOOGLE_SSO_REDIRECT = '/google/auth';
const GOOGLE_CALENDAR_AUTH = '/google/calendar_auth';
const GOOGLE_MEET_AUTH = '/google/meet_auth';
const WORKOS_ADMIN_PORTAL = '/workos-admin/admin-portal/sso';
const WORKOS_DIRECTORY_SYNC = '/workos-admin/admin-portal/dsync';
const ZOOM_CALLBACK = '/zoomcallback';

// Non-empty fallback: getServiceConfig returns the whole config object when the
// env-keyed value is falsy, so a missing .env.local must still yield a string.
const LOCAL_SLACK_CLIENT_ID = process.env.SLACK_CLIENT_ID || 'LOCAL_DEV_CLIENT_ID_HERE';

/**
 * List of functions or values for each services we use.
 * Mainly 3 methods of definition.
 * 1. Function to dynamically generate a value based on input.
 * 2. key-pair objects of values to be used per each environment.
 * 3. Raw value.
 *
 * **Please keep in alphabetical order.
 */
const serviceConfig = {
  // Note: Azure is deprecated now.
  AZURE: {
    AZURE_SSO_REDIRECT_URL: `${getConfig('API_URL')}${AZURE_SSO_REDIRECT}`
  },

  GOOGLE: {
    GOOGLE_SSO_REDIRECT_URL: `${getConfig('API_URL')}${GOOGLE_SSO_REDIRECT}`,

    GOOGLE_CALENDAR_AUTH_URL: (originOrRedirect = '') => {
      return `${getConfig('API_URL')}${GOOGLE_CALENDAR_AUTH}${originOrRedirect}`;
    },

    GOOGLE_MEET_AUTH_URL: (originOrRedirect = '') => {
      return `${getConfig('API_URL')}${GOOGLE_MEET_AUTH}${originOrRedirect}`;
    }
  },

  CALENDAR_VIEW: {
    CREATE_URL: `${getConfig('API_URL')}/calendar_view/create`
  },

  SLACK: {
    // Local dev uses your own Slack app — set SLACK_CLIENT_ID in .env.local (see .env.example).
    SLACK_CLIENT_ID: {
      staging: '369084695366.738795300098',
      development: LOCAL_SLACK_CLIENT_ID,
      production: '369084695366.397222971362',
      default: LOCAL_SLACK_CLIENT_ID
    },
    SCOPE:
      // eslint-disable-next-line max-len
      'channels:join,channels:manage,channels:read,chat:write,chat:write.public,groups:read,groups:write,im:read,im:write,links:read,links:write,team:read,users:read,users:read.email',
    LIMITED_SCOPE:
      // eslint-disable-next-line max-len
      'channels:join,channels:read,chat:write,chat:write.public,groups:read,im:read,im:write,links:read,links:write,team:read,users:read,users:read.email',
    DATABRICKS_LIMITED_SCOPE:
      // eslint-disable-next-line max-len
      'channels:read,chat:write,groups:read,im:read,im:write,links:read,links:write,team:read,users:read,users:read.email',
    USER_SCOPE: 'links:read',
    ADMIN_SCOPE: 'links:read'
  },

  WORKOS: {
    WORKOS_ADMIN_PORTAL_URL: `${getConfig('API_URL')}${WORKOS_ADMIN_PORTAL}`,
    WORKOS_DIRECTORY_SYNC_URL: `${getConfig('API_URL')}${WORKOS_DIRECTORY_SYNC}`
  },

  ZOOM: {
    ZOOM_CALLBACK_URL: (originOrRedirect = '') => {
      const API_URL = getConfig('API_URL');

      // Check if this is needed.
      if (ENV === 'development') {
        return `http://localhost:5000${API_URL}${ZOOM_CALLBACK}`;
      }

      return `${API_URL}${ZOOM_CALLBACK}`;
    }
  },

  // Event configuration
  EVENT: {
    MAX_SESSIONS_ALLOWED: {
      staging: 15,
      development: 5,
      production: 15,
      default: 15
    },
    MAX_GUESTS_ALLOWED: {
      staging: 4001,
      development: 4001,
      production: 20000,
      default: 20000
    },
    LEGACY_CUTOFF_DATE: {
      // Keeping this date early for testing on stage
      staging: '2024-12-20',
      // This is set to be after the next release date
      development: '2025-01-13',
      production: '2025-01-23',
      default: '2025-01-13'
    }
  }
};

// Get values for 3rd party service integrations.
export const getServiceConfig = (service, key, originOrRedirect = '') => {
  if (ENV && serviceConfig[service][key]) {
    const serviceValue = serviceConfig[service][key];

    // Check if 'serviceValue' is a function.
    if (serviceValue instanceof Function) {
      const test = serviceValue(originOrRedirect);
      return test;
    }

    // Check if 'serviceValue' has value for environment key.
    if (serviceValue[ENV]) return serviceValue[ENV];
  }

  // If config value is not a function and does not have specific env setting, return the value itself.
  return serviceConfig[service][key];
};
